import { TANK_DROPS_KEY, isUnvalidated, withTotals } from "./activity-entries";
import { activeFacetCount, matchesFacets } from "./activity-facets";
import type { ActivityDriver, ActivityEntry, ActivityFilter, ActivityViewFilter } from "./activity-types";

const searchText = (entry: ActivityEntry): string => [
  entry.pickup, entry.delivery,
].flatMap((transport) => transport ? [
  entry.resourceId, transport.ticketNumber, transport.resourceId, transport.siteName, transport.containerName,
  transport.siteCompanyName, transport.waterTypeName, transport.serviceTypeName, transport.wellName,
  transport.trailerName, transport.vehicleName, transport.workOrderNumber,
] : []).concat([
  entry.geofenceEvent?.siteName,
  entry.serviceWork?.siteName, entry.serviceWork?.serviceTypeName,
  entry.containerVolumeDrop?.siteName, entry.containerVolumeDrop?.containerName,
  entry.dispatch?.ticketNumber, entry.dispatch?.pickupSiteName, entry.dispatch?.deliverySiteName,
]).filter(Boolean).join(" ").toLowerCase();

const matchesView = (entry: ActivityEntry, view: ActivityViewFilter) => {
  switch (view) {
    case "attention":
      return entry.needsAttention;
    case "unmatched":
      return entry.kind === "pickup" || entry.kind === "delivery";
    case "unvalidated":
      return isUnvalidated(entry);
    default:
      return true;
  }
};

/** Applies the search, the quick view, the filters, and the row-type toggles. Drivers left with nothing are dropped while narrowing. */
export const filterDriverActivity = (drivers: ActivityDriver[], filter: ActivityFilter, now = Date.now()): ActivityDriver[] => {
  const query = filter.query?.trim().toLowerCase() ?? "";
  const view = filter.view ?? "all";
  const narrowing = query.length > 0 || view !== "all" || activeFacetCount(filter.facets) > 0;

  return drivers.flatMap((driver) => {
    const driverMatches = query.length > 0 && `${driver.name} ${driver.companyName ?? ""}`.toLowerCase().includes(query);
    const shifts = driver.shifts.map((shift) => ({
      ...shift,
      entries: shift.entries.filter((entry) => {
        if (entry.kind === "visit" && (filter.showVisits === false || view !== "all")) return false;
        if (entry.kind === "dispatch" && (filter.showDispatches === false || view !== "all")) return false;
        if (entry.kind === "siteWork" && (filter.showSiteWork === false || view !== "all")) return false;
        if (entry.kind === "tankDrop" && (filter.showTankDrops === false || (view !== "all" && view !== "attention"))) return false;
        if (!matchesView(entry, view)) return false;
        if (!matchesFacets(entry, filter.facets)) return false;
        return !query || driverMatches || searchText(entry).includes(query);
      }),
    })).filter((shift) => !narrowing || shift.entries.length > 0);

    if ((narrowing || driver.key === TANK_DROPS_KEY) && !shifts.some((shift) => shift.entries.length)) return [];
    return [withTotals(driver, shifts, now)];
  });
};
